/**
 * Format a number with thousand separators
 */
export const formatNumber = (num: number, locale = 'id-ID'): string => {
    return new Intl.NumberFormat(locale).format(num);
};

/**
 * Format area in square meters to appropriate unit
 */
export const formatArea = (sqMeters: number): string => {
    if (sqMeters >= 10000) {
        const hectares = sqMeters / 10000;
        return `${formatNumber(hectares, 'id-ID')} ha`;
    }
    return `${formatNumber(sqMeters, 'id-ID')} m²`;
};

/**
 * Format distance in meters to appropriate unit
 */
export const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
        const km = meters / 1000;
        return `${formatNumber(km, 'id-ID')} km`;
    }
    return `${formatNumber(meters, 'id-ID')} m`;
};

/**
 * Format date to Indonesian locale
 */
export const formatDate = (date: Date, options?: Intl.DateTimeFormatOptions): string => {
    const defaultOptions: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    };
    return new Intl.DateTimeFormat('id-ID', options || defaultOptions).format(date);
};

/**
 * Format date and time
 */
export const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

/**
 * Format time duration in seconds to human readable
 */
export const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
        return `${Math.round(seconds)} detik`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (remainingSeconds === 0) {
        return `${minutes} menit`;
    }
    return `${minutes} menit ${remainingSeconds} detik`;
};

/**
 * Format coordinates to standard format
 */
export const formatCoordinate = (lat: number, lng: number): string => {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(6)}° ${latDir}, ${Math.abs(lng).toFixed(6)}° ${lngDir}`;
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number, decimals = 0): string => {
    return `${value.toFixed(decimals)}%`;
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
};

/**
 * Generate a filename for PDF download
 */
export const generatePdfFilename = (prefix = 'analisis-gis'): string => {
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
    return `${prefix}-${dateStr}-${timeStr}.pdf`;
};
